import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitch, useLang } from "@/i18n";
import { can } from "@/components/staff/Panel";
import ChangeEmail from "@/components/ChangeEmail";
import { api, errMsg, fmtDateTime, ROLE_LABEL } from "@/lib/api";

function Prefs() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [p, setP] = useState(user.notification_prefs || { email: true, whatsapp: true, in_app: true });
  const [profile, setProfile] = useState({ name: user.name, phone: user.phone || "" });
  const save = async () => {
    try { const { data } = await api.put("/auth/me", { ...profile, phone: profile.phone || null, notification_prefs: p }); setUser({ ...user, ...data }); toast.success("Preferences saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5 rounded-lg border bg-card p-5 sm:p-6" data-testid="prefs-card">
      <h3 className="font-heading font-bold">{t("Profile & notifications")}</h3>
      <div className="flex items-center justify-between gap-4 text-sm" data-testid="pref-language"><span>{t("Language")}</span><LanguageSwitch /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name"><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name-input" /></Field>
        <Field label="WhatsApp number"><Input placeholder="+255…" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone-input" /></Field>
      </div>
      <label className="flex items-center justify-between gap-4 text-sm text-muted-foreground" data-testid="pref-whatsapp-unavailable"><span>{t("WhatsApp reminders: not available yet (V.A staff may contact you on WhatsApp directly)")}</span><Switch checked={false} disabled /></label>
      {[["email", "Email notifications"], ["in_app", "In-app notifications"]].map(([k, label]) => (
        <label key={k} className="flex items-center justify-between gap-4 text-sm"><span>{t(label)}</span><Switch checked={p[k]} onCheckedChange={(v) => setP({ ...p, [k]: v })} data-testid={`pref-${k}-switch`} /></label>
      ))}
      {["admin", "staff"].includes(user.role) && <label className="flex items-center justify-between gap-4 text-sm"><span>{t("Morning summary email (weekdays at 07:00)")}</span><Switch checked={p.morning !== false} onCheckedChange={(v) => setP({ ...p, morning: v })} data-testid="pref-morning-switch" /></label>}
      <p className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 shrink-0 text-primary" /> Critical security messages (password changes, account lockouts) are always delivered on all channels and cannot be disabled.</p>
      <Button onClick={save} data-testid="prefs-save-btn">{t("Save")}</Button>
    </div>
  );
}

function PasswordCard() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async () => {
    try { await api.post("/auth/change-password", f); toast.success("Password changed"); setF({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4 rounded-lg border bg-card p-5 sm:p-6">
      <h3 className="font-heading font-bold">Change password</h3>
      <Field label="Current password"><Input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="current-password-input" /></Field>
      <Field label="New password (min 8)"><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="new-password-input" /></Field>
      <Button variant="outline" onClick={save} data-testid="change-password-btn">Update password</Button>
    </div>
  );
}

function SystemSettings() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [buffer, setBuffer] = useState(7);
  const [sched, setSched] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => { setDays(r.data.expiry_warning_days); setBuffer(r.data.internal_buffer_days); setSched(r.data); }); }, []);
  const save = () => api.put("/settings", { expiry_warning_days: Number(days), internal_buffer_days: Number(buffer) }).then(() => toast.success("Settings saved")).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-4 rounded-lg border bg-card p-5 sm:p-6" data-testid="system-settings-card">
      <h3 className="font-heading font-bold">System settings</h3>
      <Field label="Default document expiry warning period (days)"><Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} data-testid="settings-warning-days-input" /></Field>
      <p className="text-xs text-muted-foreground">Documents are marked “Expiring Soon” within this many days of expiry, unless a document has its own warning period.</p>
      <Field label="Default internal deadline buffer (days)"><Input type="number" min={0} max={365} value={buffer} onChange={(e) => setBuffer(e.target.value)} data-testid="settings-buffer-days-input" /></Field>
      <p className="text-xs text-muted-foreground">Suggested internal deadline = official deadline minus this buffer, unless the template sets its own buffer. Official deadlines never change.</p>
      {can(user, "system_settings") && <Button variant="outline" onClick={save} data-testid="settings-save-btn">Save settings</Button>}
      {sched && (
        <div className="space-y-1 border-t pt-4 text-sm" data-testid="scheduler-status">
          <div className="font-semibold">Daily job (06:00 Tanzania time)</div>
          <div>Last scheduled run: <strong data-testid="last-scheduled-run">{sched.last_scheduled_run ? fmtDateTime(sched.last_scheduled_run.at) : "None recorded"}</strong></div>
          <div>Last manual run: {sched.last_manual_run ? fmtDateTime(sched.last_manual_run.at) : "None"}</div>
          <div className={sched.scheduler_verified ? "text-emerald-700" : "text-amber-700"} data-testid="scheduler-verified">{sched.scheduler_verified ? "Automatic scheduling verified" : "Not verified: no automatic run recorded yet"}</div>
          {can(user, "jobs_run") && <Button size="sm" variant="ghost" onClick={() => api.post("/cron/run-now").then(() => toast.success("Manual run started (recorded as manual)")).catch((e) => toast.error(errMsg(e)))} data-testid="cron-run-now-btn">Run now (manual)</Button>}
        </div>
      )}
    </div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/notifications").then((r) => setItems(r.data.items)); }, []);
  return (
    <div>
      <PageHeader title="Account" subtitle={`${user.email} · ${ROLE_LABEL[user.role]}`}>{["admin", "staff"].includes(user.role) && <Button variant="outline" asChild data-testid="account-my-performance"><a href="/me">My Workspace</a></Button>}<Button variant="outline" onClick={logout} data-testid="account-logout-btn">Sign out</Button></PageHeader>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6"><Prefs /><PasswordCard /><ChangeEmail />{(can(user, "system_settings") || can(user, "jobs_run")) && <SystemSettings />}</div>
        <div className="rounded-lg border bg-card" data-testid="notifications-list">
          <div className="border-b px-5 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Recent notifications</div>
          {!items.length && <p className="p-5 text-sm text-muted-foreground">No notifications yet.</p>}
          {items.map((n) => (
            <div key={n.id} className="border-b px-5 py-3 last:border-0">
              <div className="flex items-center gap-2 text-sm font-semibold">{n.critical && <ShieldCheck className="h-3.5 w-3.5 text-red-600" />}{n.title}</div>
              {n.body && <p className="text-xs text-muted-foreground">{n.body}</p>}
              <p className="mt-1 text-[11px] text-muted-foreground">{fmtDateTime(n.created_at)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
